// chatContext/chatContextKey.ts
//
// One string that names what the composer's context pill is showing (ORA-662),
// so "the student removed this" can be remembered without holding on to the
// anchor object itself.
//
// Detach is sticky per item, not per message: the key is what decides when it
// stops applying. A content pill is keyed by the anchor's kind and id — the
// next question, the next card, the next video gets its own key and so comes
// back attached. A page pill has no anchor, so the route id is the key.

import type {
  ChatAnchorInput,
  ChatAnchorRef,
  ChatRouteId,
} from '../types/chatEnvelope';

/** The id inside an anchor input, whatever its kind calls it. */
const anchorId = (anchor: ChatAnchorInput): string => {
  switch (anchor.type) {
    case 'question':
      return anchor.vignetteId;
    case 'flashcard':
      return anchor.flashcardId;
    case 'video':
      return anchor.videoId;
    case 'article':
      return anchor.articleId;
    case 'lecture':
      return anchor.lectureId;
    case 'exam':
      return anchor.examId;
  }
};

/**
 * The key for the item on screen. Deliberately not the variant: a question
 * re-rendered with a different variant is still the same question, and detach
 * should survive it.
 */
export const chatAnchorContextKey = (anchor: ChatAnchorInput): string =>
  `${anchor.type}:${anchorId(anchor)}`;

/** The same key, for an anchor that is already in its wire shape. */
export const chatAnchorRefContextKey = (anchor: ChatAnchorRef): string =>
  `${anchor.type}:${anchor.id}`;

/** The key for a page that has no anchor of its own. */
export const chatPageContextKey = (route: ChatRouteId): string =>
  `page:${route}`;

/** Whichever of the two is in force right now. */
export const chatContextKey = (
  anchor: ChatAnchorInput | null,
  route: ChatRouteId
): string =>
  anchor ? chatAnchorContextKey(anchor) : chatPageContextKey(route);
